import { useForm } from "@tellescope/forms";
import { useTellescopeInfiniteQuery, useTellescopeMutation } from "@tellescope/tanstack-query";
import type { CalendarEventReminderInfoForType, CalendarEventReminderNotificationInfo } from "@tellescope/types-models";
import {
  Button,
  Checkbox,
  cn,
  Combobox,
  type ComboboxOption,
  Input,
  Label,
  Textarea,
  useStateWithSetAction,
} from "@tellescope/ui";
import { Spinner, Select } from "@tellescope/design-system";
import { Plus } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type NotificationReminder =
  | CalendarEventReminderInfoForType["enduser-notification"]
  | CalendarEventReminderInfoForType["user-notification"];

const convertStringToReminderChannel = (str: string): CalendarEventReminderNotificationInfo["channel"] => {
  switch (str) {
    case "Email":
    case "SMS":
      return str;
  }

  return undefined;
};

export interface NotificationInfoSectionProps<T extends NotificationReminder> {
  value: T;
  onChange: (newValue: T) => void;
  mode: "enduser" | "team";
}

export const NotificationInfoSection = <T extends NotificationReminder>({
  value,
  onChange,
  mode,
}: NotificationInfoSectionProps<T>) => {
  const { t } = useTranslation("calendar");

  const templateQuery = useTellescopeInfiniteQuery({
    model: "templates",
    method: "getSome",
    request: {
      filter: {
        type: mode,
      },
    },
    fetchAllPages: true,
    select: (data) => data.pages.flat(),
  });
  const createTemplateMutation = useTellescopeMutation({
    model: "templates",
    method: "createOne",
    onSuccess: (res) => {
      setShowTemplateCreation(false);
      onChange({
        ...value,
        info: {
          ...value.info,
          templateId: res.id,
        },
      });
    },
  });
  const selectedTemplate = useMemo(() => {
    if (!value.info.templateId) return undefined;
    return templateQuery.data?.find(({ id }) => id === value.info.templateId);
  }, [value.info.templateId, templateQuery.data]);
  const templateOptions = useMemo<ComboboxOption[]>(
    () =>
      templateQuery.data?.map((template) => ({
        value: template.id,
        label: template.title,
      })) ?? [],
    [templateQuery.data]
  );
  const selectedTemplateOption = useMemo<ComboboxOption | undefined>(() => {
    if (!selectedTemplate) return undefined;
    return {
      value: selectedTemplate.id,
      label: selectedTemplate.title,
    };
  }, [selectedTemplate]);

  const templateCreationForm = useForm({
    fields: {
      title: {
        type: "text",
        initialValue: selectedTemplate?.title ? `${selectedTemplate.title} (2)` : "",
        required: true,
      },
      subject: {
        type: "text",
        initialValue: value.info.channel === "Email" ? selectedTemplate?.subject : "",
      },
      message: {
        type: "textarea",
        initialValue: selectedTemplate?.message ?? "",
      },
      isMarketing: {
        type: "checkbox",
        initialValue: selectedTemplate?.isMarketing ?? false,
      },
    },
  });
  const [showTemplateCreation, setShowTemplateCreation] = useStateWithSetAction(false, (newVal) => {
    if (!newVal) {
      templateCreationForm.resetForm();
    }
  });

  return (
    <>
      <div className="flex items-center gap-1">
        <Select
          value={value.info.channel ?? "Email"}
          onValueChange={(channel) =>
            onChange({
              ...value,
              info: {
                ...value.info,
                channel: convertStringToReminderChannel(channel) ?? value.info.channel,
              },
            })
          }
        >
          <Select.Trigger className="flex-shrink">
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="Email">{t("Email")}</Select.Item>
            <Select.Item value="SMS">{t("SMS")}</Select.Item>
          </Select.Content>
        </Select>
        <Combobox
          className="flex-1 min-w-0"
          value={selectedTemplateOption}
          onChange={(option) =>
            onChange({
              ...value,
              info: {
                ...value.info,
                templateId: option?.value,
              },
            })
          }
          options={templateOptions}
          placeholder={t("No template")}
          searchPlaceholder={t("Search templates...")}
          isLoading={templateQuery.isLoading}
          loadingText={t("Loading templates...")}
          emptyText={t("No templates found")}
        />
        <Button
          variant="outline"
          onClick={() => setShowTemplateCreation((prev) => !prev)}
          className={showTemplateCreation ? "text-destructive border-destructive" : undefined}
        >
          <Plus className={cn("transition-all", showTemplateCreation ? "rotate-45" : undefined)} />
        </Button>
      </div>
      {showTemplateCreation && (
        <div className={cn("relative transition-all border rounded-md overflow-hidden space-y-2 p-3")}>
          {createTemplateMutation.isPending && (
            <div className="absolute top-0 left-0 right-0 bottom-0 bg-gray-100 bg-opacity-50 backdrop-blur-[1px] flex flex-col gap-2 items-center justify-center z-20">
              <Spinner size="sm" />
              <p className="text-sm text-muted-foreground">{t("Creating template...")}</p>
            </div>
          )}
          <h4 className="text-xs font-semibold">{t("New template")}</h4>
          <Input placeholder={t("Template name")} {...templateCreationForm.getFieldProps("title")} />
          {(value.info.channel === "Email" || value.info.channel === undefined) && (
            <Input placeholder={t("Email subject")} {...templateCreationForm.getFieldProps("subject")} />
          )}
          <Textarea placeholder={t("Message body...")} {...templateCreationForm.getFieldProps("message")} />
          <span className="flex gap-1 items-center">
            <Checkbox
              checked={templateCreationForm.values.isMarketing}
              onCheckedChange={(checked) => templateCreationForm.setFieldValue("isMarketing", !!checked)}
              id="is-marketing-message"
            />
            <Label htmlFor="is-marketing-message" className="text-xs text-muted-foreground">
              {t("Is marketing message")}
            </Label>
          </span>
          <div className="flex gap-2">
            <Button
              disabled={!templateCreationForm.isValid}
              size="sm"
              onClick={() => {
                createTemplateMutation.mutate({
                  ...templateCreationForm.values,
                  subject: value.info.channel === "SMS" ? "" : templateCreationForm.values.subject,
                });
              }}
            >
              {t("Save template")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                setShowTemplateCreation(false);
              }}
            >
              {t("Cancel")}
            </Button>
          </div>
        </div>
      )}
      <span className="flex gap-2 items-center">
        <Checkbox
          id="send-even-if-passed"
          checked={!value.dontSendIfPassed}
          onCheckedChange={(checked) =>
            onChange({
              ...value,
              dontSendIfPassed: !checked,
            })
          }
        />
        <Label htmlFor="send-even-if-passed" className="text-muted-foreground text-xs font-light">
          {t("Send even if timing has passed")}
        </Label>
      </span>
    </>
  );
};
